import React, { useEffect, useState, useCallback } from 'react';
import { getAuthHeaders, API_URL } from '../services/auth';
import type { TemplateContext } from '../App';
import './BindingBanner.css';

/**
 * BindingBanner — sticky top banner that resolves and surfaces the current
 * document's "Artemis binding state" the moment a Word document is opened.
 *
 * Why this exists
 * ---------------
 * Users can open a template .docx from anywhere — File Explorer, email,
 * OneDrive sync, a teammate handoff — not just from the taskpane's Edit
 * button. In those cases the file already carries dp_* custom properties
 * baked in, but the taskpane has no idea which template the file claims to
 * be until something asks the server. Without this banner the user can
 * sit in front of a "Bond Status Report v6 (Draft)" file thinking they're
 * about to update template 1081, when in reality:
 *   - they're in the wrong tenant   → orphan
 *   - they're in the wrong env      → rebind (dev's Id=1081 ≠ prod's)
 *   - the template was archived     → orphan
 *   - someone else saved v15        → drift (behind)
 *   - the file is in Protected View → none of their edits would save
 *
 * State machine (mirrors the /templates/verify-binding response)
 * --------------------------------------------------------------
 *   protected-view → file is read-only; nothing else matters until they
 *                    click "Enable Editing" in Word's yellow bar
 *   loading        → first paint, still resolving
 *   hidden         → no dp_*, not protected; this is a fresh authoring
 *                    session, banner should be invisible (no noise)
 *   match          → file ↔ server agree (happy path) — show as a calm
 *                    green confirmation, dismissible
 *   legacy         → pre-PublicId file; we resolved by Id only. Tell the
 *                    user it'll be upgraded on next save.
 *   rebind         → PublicId resolves to a DIFFERENT local Id (the
 *                    typical "promoted from dev" case). Offer to update
 *                    the binding before they save.
 *   orphan         → No template in this tenant matches. Offer "Save as
 *                    new" — anything else would just 404.
 *   error          → network/auth issue. Stay quiet; show retry.
 *
 * Drift handling
 * --------------
 * On top of any "match/legacy/rebind" state, the server reports
 * versionDrift = 'same' | 'behind' | 'ahead'. 'behind' means the file is
 * an older version than what's now on the server (likely someone else
 * saved a newer version). Surface that prominently because saving would
 * conflict (409). Today we just embed it inside the main banner.
 */

type Drift = 'same' | 'behind' | 'ahead' | 'unknown';

interface VerifyResponse {
  status: 'unbound' | 'match' | 'legacy' | 'rebind' | 'orphan';
  reason?: string;
  template?: {
    id: number;
    name: string;
    publicId: string;
    status: string;
    documentType: string;
    currentVersion: number;
    hasDraft: boolean;
    draftVersion: number | null;
    publishedVersion: number | null;
    deletedAt: string | null;
  };
  clientVersion?: number | null;
  latestVersion?: number | null;
  latestVersionStatus?: string | null;
  latestUploadedAt?: string | null;
  latestUploadedBy?: string | null;
  versionDrift?: Drift;
  clientTemplateId?: number;
}

interface LocalBinding {
  templateId?: number;
  templateName?: string;
  publicId?: string;
  versionNumber?: number;
  versionStatus?: string;
}

type BannerState =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'protected' }
  | { kind: 'error'; message: string }
  | { kind: 'resolved'; verify: VerifyResponse; local: LocalBinding };

interface BindingBannerProps {
  /** Current active template (from App-level state). When set, we trust it
   *  for the initial paint and run verify in the background to confirm. */
  activeTemplate: TemplateContext | null;
  /** Called when the user clicks "Rebind" — the parent should update
   *  activeTemplate to the new (id, publicId) and re-stamp dp_* on the
   *  document so subsequent saves target the correct template. */
  onRebind: (next: TemplateContext) => void;
  /** Called when the user clicks "Save as new template" — parent should
   *  navigate to the save view in "save-as-new" mode. */
  onSaveAsNew: () => void;
  /** Called when the user clicks "Reload latest version" on a behind-drift
   *  banner — parent should re-fetch the latest version into Word. */
  onReloadLatest: (templateId: number) => void;
}

export function BindingBanner({
  activeTemplate,
  onRebind,
  onSaveAsNew,
  onReloadLatest,
}: BindingBannerProps) {
  const [state, setState] = useState<BannerState>({ kind: 'loading' });
  const [dismissed, setDismissed] = useState(false);

  // Re-run the resolve flow. Exposed so the user can click "retry" on
  // an error banner without remounting the whole pane.
  const resolve = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      // 1. Protected View / read-only detection. Office.context.document.mode
      //    exposes 'readOnly' for Outlook-attachment and downloaded-from-
      //    internet files that Word opens in its yellow-bar Protected View.
      //    We deliberately don't TRY a write to probe — that would mutate
      //    state on Word and is forbidden without explicit permission.
      try {
        const mode = (Office?.context?.document as any)?.mode;
        if (mode === 'readOnly') {
          setState({ kind: 'protected' });
          return;
        }
      } catch { /* fall through; assume editable */ }

      // 2. Determine the local binding.
      //
      //    Source of truth, in priority order:
      //    (a) the in-memory activeTemplate prop, IF it carries a publicId.
      //        After a save, App.persistTemplate writes dp_* into the doc
      //        ASYNCHRONOUSLY while ALSO updating activeTemplate. Reading
      //        the doc here would race that write and could still see the
      //        pre-save state (this is exactly the "banner stays legacy
      //        after save" bug). The prop is already correct in memory, so
      //        we trust it.
      //    (b) otherwise, read dp_* from the document — the desktop-open /
      //        fresh-load case where there's no activeTemplate yet.
      let local: LocalBinding;
      if (activeTemplate?.id && activeTemplate?.publicId) {
        local = {
          templateId: activeTemplate.id,
          templateName: activeTemplate.name,
          publicId: activeTemplate.publicId,
          versionNumber: activeTemplate.versionNumber,
          versionStatus: activeTemplate.versionStatus,
        };
      } else {
        local = await new Promise<LocalBinding>((resolve) => {
          Word.run(async (context) => {
            const props = context.document.properties.customProperties;
            props.load('items');
            await context.sync();
            const get = (k: string) => {
              const p = props.items.find((it: any) => it.key === k);
              return p?.value ? String(p.value) : undefined;
            };
            resolve({
              templateId: get('dp_templateId') ? Number(get('dp_templateId')) : undefined,
              templateName: get('dp_templateName'),
              publicId: get('dp_publicId'),
              versionNumber: get('dp_versionNumber') ? Number(get('dp_versionNumber')) : undefined,
              versionStatus: get('dp_versionStatus'),
            });
          }).catch(() => resolve({}));
        });
      }

      // 3. Nothing in the doc → caller is authoring fresh. Stay invisible.
      if (!local.templateId && !local.publicId) {
        setState({ kind: 'hidden' });
        return;
      }

      // 4. Server-resolve the binding. The endpoint is tenant-scoped via
      //    apiKeyAuth, so cross-tenant ids will simply look like 'orphan'.
      const resp = await fetch(`${API_URL}/templates/verify-binding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          publicId: local.publicId,
          templateId: local.templateId,
          versionNumber: local.versionNumber,
        }),
      });
      if (!resp.ok) {
        setState({ kind: 'error', message: `verify-binding ${resp.status}` });
        return;
      }
      const json = await resp.json();
      if (!json?.success) {
        setState({ kind: 'error', message: json?.error?.message || 'verify-binding failed' });
        return;
      }
      const verify = json.data as VerifyResponse;
      if (verify.status === 'unbound') {
        // Server agrees we have no useful binding — render nothing.
        setState({ kind: 'hidden' });
        return;
      }
      setState({ kind: 'resolved', verify, local });
    } catch (err: any) {
      setState({ kind: 'error', message: err?.message || 'verify-binding failed' });
    }
    // Depends on activeTemplate so the closure always reads the current
    // binding (priority-(a) branch above). Without this, a save would
    // re-run a stale resolve() that still sees activeTemplate === null.
  }, [activeTemplate?.id, activeTemplate?.publicId, activeTemplate?.versionNumber]);

  // Run on mount, and re-run whenever resolve() identity changes — which is
  // exactly when activeTemplate's id / publicId / versionNumber change
  // (e.g. after Edit loads a template, or a save bumps the version).
  useEffect(() => {
    resolve();
  }, [resolve]);

  if (dismissed) return null;
  if (state.kind === 'hidden') return null;

  // --- Render branches -----------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <div className="dp-binding-banner dp-binding-banner--loading">
        <span className="dp-binding-banner__icon">⏳</span>
        <span>Checking template binding…</span>
      </div>
    );
  }

  if (state.kind === 'protected') {
    return (
      <div className="dp-binding-banner dp-binding-banner--protected">
        <span className="dp-binding-banner__icon">🔒</span>
        <span>
          <strong>Protected View.</strong> Word opened this file as read-only.
          Click <em>Enable Editing</em> in Word's yellow bar before saving.
        </span>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="dp-binding-banner dp-binding-banner--error">
        <span className="dp-binding-banner__icon">⚠</span>
        <span>Couldn't verify template binding: {state.message}.</span>
        <button className="dp-binding-banner__btn" onClick={resolve}>Retry</button>
        <button className="dp-binding-banner__dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
      </div>
    );
  }

  const { verify, local } = state;
  const t = verify.template;
  const drift: Drift = verify.versionDrift ?? 'unknown';
  const driftBehind = drift === 'behind';

  // --- ORPHAN: file's binding doesn't resolve in this tenant -------------
  if (verify.status === 'orphan') {
    return (
      <div className="dp-binding-banner dp-binding-banner--orphan">
        <div className="dp-binding-banner__row">
          <span className="dp-binding-banner__icon">🚫</span>
          <span>
            <strong>This document's template doesn't exist here.</strong>{' '}
            It claims to be <em>{local.templateName || `template #${local.templateId}`}</em>
            {verify.reason ? <> — {verify.reason}</> : null}.
            You can still author and save it as a new template.
          </span>
        </div>
        <div className="dp-binding-banner__actions">
          <button className="dp-binding-banner__btn dp-binding-banner__btn--primary" onClick={onSaveAsNew}>
            Save as new template
          </button>
          <button className="dp-binding-banner__dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
        </div>
      </div>
    );
  }

  // --- REBIND: PublicId resolved to a different local Id -----------------
  if (verify.status === 'rebind' && t) {
    return (
      <div className="dp-binding-banner dp-binding-banner--rebind">
        <div className="dp-binding-banner__row">
          <span className="dp-binding-banner__icon">🔀</span>
          <span>
            <strong>Template moved.</strong> This file was authored against
            id <code>{verify.clientTemplateId}</code> but in this environment
            the same template lives at id <code>{t.id}</code>{' '}
            (<em>{t.name}</em>). Rebind before saving, or save as a new
            template.
          </span>
        </div>
        <div className="dp-binding-banner__actions">
          <button
            className="dp-binding-banner__btn dp-binding-banner__btn--primary"
            onClick={() => onRebind({
              id: t.id,
              name: t.name,
              versionNumber: verify.latestVersion ?? t.currentVersion ?? 1,
              versionStatus: (verify.latestVersionStatus as TemplateContext['versionStatus']) || 'Draft',
              isLocked: false,
              publicId: t.publicId,
            })}
          >
            Rebind to id {t.id}
          </button>
          <button className="dp-binding-banner__btn" onClick={onSaveAsNew}>
            Save as new
          </button>
          <button className="dp-binding-banner__dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
        </div>
      </div>
    );
  }

  // --- MATCH / LEGACY: happy path or pre-PublicId binding ----------------
  if ((verify.status === 'match' || verify.status === 'legacy') && t) {
    const variantClass = verify.status === 'legacy' ? 'dp-binding-banner--legacy' : 'dp-binding-banner--match';
    const versionLabel = local.versionNumber ? ` v${local.versionNumber}` : '';
    const statusLabel = local.versionStatus ? ` (${local.versionStatus})` : '';

    return (
      <div className={`dp-binding-banner ${variantClass}`}>
        <div className="dp-binding-banner__row">
          <span className="dp-binding-banner__icon">
            {verify.status === 'legacy' ? 'ℹ' : '✓'}
          </span>
          <span>
            <strong>{t.name}</strong>{versionLabel}{statusLabel}
            {verify.status === 'legacy' && (
              <> — legacy binding; will be upgraded on next save.</>
            )}
            {driftBehind && verify.latestVersion && (
              <> &nbsp;Server has v{verify.latestVersion}; your copy is behind.</>
            )}
            {/* Ctrl+S reminder. Word's own save writes only the local .docx
                file — it does NOT push a version to Artemis. Office.js
                can't intercept Ctrl+S, so the best we can do is tell the
                user which save does what, every time they're looking at a
                bound template. */}
            <span className="dp-binding-banner__hint">
              Ctrl+S saves only this file — use <strong>Save Template</strong> to update Artemis.
            </span>
          </span>
        </div>
        {driftBehind && verify.latestVersion && (
          <div className="dp-binding-banner__actions">
            <button
              className="dp-binding-banner__btn dp-binding-banner__btn--primary"
              onClick={() => onReloadLatest(t.id)}
            >
              Reload v{verify.latestVersion}
            </button>
            <button className="dp-binding-banner__dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
          </div>
        )}
        {!driftBehind && (
          <button className="dp-binding-banner__dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
        )}
      </div>
    );
  }

  // Fallback (shouldn't happen if status enum is exhaustive).
  return null;
}
